/**
 * Google Meet host selection concern for the Details step.
 *
 * The Google-Meet-external-host feature lets a chosen person host the Meet while
 * the event stays on the scheduler's calendar. Like Zoom (see
 * `useScheduleHostSelection`), the host may only be one of the picked
 * interviewers or the scheduler — the difference is the eligibility signal: a
 * person can host only if they have granted the Google Meet scope.
 *
 * Responsibilities (scoped to `location === 'google_meet'` + flag on):
 *   - Build the set of Meet-eligible ids INLINE from the picker's own candidates
 *     (each interviewer/scheduler carries `meetEligible` from the interviewers
 *     API) — no separate fetch.
 *   - Auto-pick a default host when none is set: the scheduler if eligible,
 *     else the first eligible interviewer. A still-valid user pick — and an
 *     edit-saved host — is preserved, never substituted.
 *
 * Flag off or another location → no writes, zero change.
 */
import { useEffect, useMemo } from 'react';
import type { RefObject } from 'react';
import type { FormBuilderHandle } from '@kula-ai/components';
import type { ApiReference } from '../../../config/types';

interface HostCandidate {
  value: string;
  label?: string;
  image?: string;
  /** Google Meet host eligibility (BE `can_host_gmeet_meeting`), threaded onto
   *  the option so eligibility is read inline — no active_hosts fetch. */
  meetEligible?: boolean;
}

interface UseGmeetHostSelectionArgs {
  /** `tGoogleMeetExternalHost`. */
  enabled: boolean;
  /** Live form location (mirrored in DetailsForm; fires on programmatic
   *  `setValue` too, unlike `sharedState.location`). */
  location: string;
  /** The location the edit hydrated with (`defaultValues.location`). In edit
   *  mode, the auto-pick is skipped while the live location still equals this —
   *  it exists only to seed a host when the user SWITCHES to Google Meet
   *  mid-edit, not to fabricate one on an untouched Meet interview. Undefined on
   *  create (auto-pick always allowed). */
  initialLocation?: string;
  apiReference: ApiReference;
  formRef: RefObject<FormBuilderHandle | null>;
  /** Picked interviewers — host candidates, in order. */
  participantOptions: HostCandidate[];
  /** The scheduler — a host candidate; `meetEligible` from `user_zoom_status`. */
  currentUser?: { id: number | string; meetEligible?: boolean };
  /** Plan-level host strategy (`google_host_type`), identical to the Zoom
   *  auto-pick order: `'organizer'` tries the scheduler first, anything else
   *  (including nil) tries the first interviewer first. */
  planHostType?: 'organizer' | 'interviewer' | string | null;
  /** When set, the host is LOCKED to this id (the organizer) — self-schedule
   *  one-on-one with multiple interviewers, where the interviewer isn't decided
   *  until the candidate books. Overrides the eligibility auto-pick; the field
   *  is rendered disabled by `buildGmeetHostField`. */
  lockedHostId?: string;
}

interface UseGmeetHostSelectionResult {
  /** Ids (as strings) of users who have granted the Google Meet scope. Fed to
   *  `buildGmeetHostField` to mark ineligible interviewers/scheduler disabled. */
  meetEligibleIds: Set<string>;
  /** Always false — eligibility is inline (no fetch); kept for signature parity. */
  gmeetHostsLoading: boolean;
  /** Always false — eligibility is inline (no fetch to fail); kept for parity. */
  eligibilityUnknown: boolean;
}

export function useGmeetHostSelection({
  enabled,
  location,
  initialLocation,
  apiReference,
  formRef,
  participantOptions,
  currentUser,
  planHostType,
  lockedHostId,
}: UseGmeetHostSelectionArgs): UseGmeetHostSelectionResult {
  const isGmeetLocation = enabled && location === 'google_meet';

  // Eligible-id set built INLINE from the picker's own candidates — interviewers
  // carry `meetEligible` from the interviewers API (via `participant_default_options`
  // and the interviewer option cache), the scheduler from `user_zoom_status`. No
  // active_hosts fetch: eligibility rides with data the picker already has, so it's
  // bounded to the panel and never truncated by an account-wide list.
  const meetEligibleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const o of participantOptions) {
      if (o.meetEligible && o.value) ids.add(String(o.value));
    }
    if (currentUser?.meetEligible) ids.add(String(currentUser.id));
    return ids;
  }, [participantOptions, currentUser]);

  // No fetch → never loading, eligibility never "unknown" (kept in the result
  // shape so the field builder's signature is unchanged).
  const gmeetHostsLoading = false;
  const eligibilityUnknown = false;

  // Auto-pick the default host once the eligible set resolves: scheduler if
  // eligible, else the first eligible interviewer. Never overwrites a
  // still-valid pick, and never substitutes an edit-saved host — it only fills
  // when Host is empty or the current value is no longer an eligible candidate.
  useEffect(() => {
    if (!isGmeetLocation) return;
    const formApi = formRef.current;
    if (!formApi) return;
    const values = formApi.getValues() as Record<string, unknown>;
    // Guard a stale run after the live location already moved off Meet.
    if (values.location !== 'google_meet') return;

    const current = String(values.host_id ?? '');

    // Locked (self-schedule one-on-one + multiple interviewers): force the host
    // to the organizer and skip the eligibility auto-pick. Runs even while the
    // eligible-set is still fetching — the lock doesn't depend on it.
    if (lockedHostId) {
      if (current !== lockedHostId) {
        formApi.setValue('host_id', lockedHostId, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      return;
    }

    // Edit with the location unchanged from hydration: never fabricate a host.
    // Auto-pick exists to seed a host when the user SWITCHES to Google Meet
    // mid-edit (that location change opens its own confirmation). A pre-feature
    // Meet interview hydrates host_id '' (the host didn't exist when it was
    // scheduled); auto-filling here would register a phantom host change and
    // open the invites dialog on an otherwise no-op save.
    if (apiReference === 'edit' && location === initialLocation) return;

    // Preference order mirrors the Zoom auto-pick (`pickAutoHostId`):
    // `zoom_host_type === 'organizer'` tries the scheduler first; otherwise the
    // first interviewer first.
    const schedulerId = currentUser === undefined ? '' : String(currentUser.id);
    const interviewerIds = participantOptions.map((o) => String(o.value));
    const candidateIds =
      planHostType === 'organizer'
        ? [...(schedulerId ? [schedulerId] : []), ...interviewerIds]
        : [...interviewerIds, ...(schedulerId ? [schedulerId] : [])];
    // Prefer whoever actually has the Meet scope — someone with access should
    // always win over a scope-less scheduler. Only when NOBODY on the interview
    // has the scope does the scheduler become the pick, so the meeting still
    // gets created (BE falls back to a scheduler-hosted meeting).
    const scopedCandidateIds = candidateIds.filter((id) =>
      meetEligibleIds.has(id)
    );
    const fallbackId = schedulerId || '';

    // Keep a still-valid pick — one with the Meet scope, or the scheduler (who
    // is always a valid host). An INVALID current host is dropped and re-picked:
    // e.g. a Zoom host carried over when the location switches to Google Meet who
    // has no Meet scope — keeping it would fail validation on a host that can't
    // host here. Edit with an UNCHANGED location already returned above, so a
    // saved host is still preserved there; this only re-picks when the host
    // genuinely can't host the current provider.
    const currentStillValid =
      scopedCandidateIds.includes(current) || current === fallbackId;
    if (current && currentStillValid) return;

    const next =
      scopedCandidateIds.length > 0 ? scopedCandidateIds[0] : fallbackId;
    if (next && next !== current) {
      formApi.setValue('host_id', next, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [
    isGmeetLocation,
    apiReference,
    location,
    initialLocation,
    meetEligibleIds,
    formRef,
    currentUser,
    participantOptions,
    planHostType,
    lockedHostId,
  ]);

  return { meetEligibleIds, gmeetHostsLoading, eligibilityUnknown };
}
